import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { TestComponentFilterDTO, TestScenarioDTO, TestScenarioFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { TestComponentDTO } from '../../../shared/interfaces/testingSphere/TestComponentDTO';
import { TestAcceptanceCriteriaDTO } from '../../../shared/interfaces/testingSphere/TestAcceptanceCriteriaDTO';
import { TestAcceptanceCriteriaFilterDTO } from '../../../shared/interfaces/testingSphere/TestAcceptanceCriteriaFilterDTO';
import { TestAcceptanceCriteriaMaintenance } from '../..';

interface ITestAcceptanceCriteriaProps {
    data: any;
    testScenarioId?: string;
}

export const TestAcceptanceCriteria: React.FC<ITestAcceptanceCriteriaProps> = ({ data, testScenarioId }) => {

    const [testScenarioList, setTestScenarioList] = useState<TestScenarioDTO[]>([]);
    const [testComponentList, setTestComponentList] = useState<TestComponentDTO[]>([]);
    const [testAcceptanceCriteriaList, setTestAcceptanceCriteriaList] = useState<TestAcceptanceCriteriaDTO[]>([]);
    const [testAcceptanceCriteriaFilterDTO, setTestAcceptanceCriteriaFilterDTO] = useState<TestAcceptanceCriteriaFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');

    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    useEffect(() => {
        setTestAcceptanceCriteriaFilterDTO({
            ...testAcceptanceCriteriaFilterDTO,
            id: '',
            testComponentId: testScenarioId ? '' : data.id ?? '',
            testScenarioId: testScenarioId ? data.id : '',
            conditionId: '',
            testAttributeValueId: '',
            outputTestAttributeValueId: '',
            createdById: ''
        } as TestAcceptanceCriteriaDTO);
    }, []);

    useEffect(() => {
        handleTestScenarioGetAllAsync();
        handleTestAcceptanceCriteriatGetAllViewAsync();
    }, [testAcceptanceCriteriaFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleTestAcceptanceCriteriatGetAllViewAsync();
    }, [actionExecuted]);

    useEffect(() => {
        handleTestComponentGetAllAsync();
    }, [testAcceptanceCriteriaFilterDTO?.testScenarioId]);

    const handleTestScenarioGetAllAsync = useCallback(async () => {
        const testScenarioFilterDTO: TestScenarioFilterDTO = {
            name: '',
            testPackageId: '',
        };
        GenericService.GetAllAsync1('testingsphere.testscenario', testScenarioFilterDTO, setLoading, setTestScenarioList, showMessage);
    }, []);

    const handleTestComponentGetAllAsync = useCallback(async () => {
        const testComponentFilterDTO: TestComponentFilterDTO = {
            name: '',
            testScenarioId: testAcceptanceCriteriaFilterDTO?.testScenarioId,
        };
        GenericService.GetAllAsync1('testingsphere.testcomponent', testComponentFilterDTO, setLoading, setTestComponentList, showMessage);
    }, [testAcceptanceCriteriaFilterDTO]);

    const handleTestAcceptanceCriteriatGetAllViewAsync = useCallback(async () => {
        if (testAcceptanceCriteriaFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('testingsphere.testAcceptanceCriteria', testAcceptanceCriteriaFilterDTO, setActionExecuted, setLoading, setTestAcceptanceCriteriaList, showMessage, true);
    }, [testAcceptanceCriteriaFilterDTO]);

    const handleClearFilterClick = () => {
        setTestAcceptanceCriteriaFilterDTO({ ...testAcceptanceCriteriaFilterDTO, testComponentId: testScenarioId ?? data.id } as TestAcceptanceCriteriaFilterDTO);
    };

    function handleSearchClick() {
        handleTestAcceptanceCriteriatGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);

    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Test Acceptance Critera', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: testAcceptanceCriteriaList.length },
        { label: 'TestScenario', value: !data ? testScenarioList.find(x => x.id === testAcceptanceCriteriaFilterDTO?.testScenarioId)?.name : undefined },
        { label: 'Test Component', value: !data ? testComponentList.find(x => x.id === testAcceptanceCriteriaFilterDTO?.testComponentId)?.name : undefined },
    ];

    const columns: ICardColumns[] = [
        { field: 'testComponent', label: 'Test Component', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'testAttributeValue', label: 'Test Attribute', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'conditionName', label: 'Condition', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'outputTestAttributeValue', label: 'Output Test Attribute', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: false },
        { field: 'valueToComparation', label: 'Value to Comparation', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='TESTCENARIO'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        {/* {!data && ( */}
                        <React.Fragment>
                            <Grid item xs={12} md={4}>
                                <SelectField
                                    id='testScenarioId'
                                    label='TestScenario Name'
                                    value={testAcceptanceCriteriaFilterDTO?.testScenarioId}
                                    setValue={setTestAcceptanceCriteriaFilterDTO}
                                    obj={testAcceptanceCriteriaFilterDTO}
                                    valueList={testScenarioList}
                                />
                            </Grid>
                        </React.Fragment>
                        {/* )} */}
                        <Grid item xs={12} md={4}>
                            <SelectField
                                id='testComponentId'
                                label='Test Component Name'
                                value={testAcceptanceCriteriaFilterDTO?.testComponentId}
                                setValue={setTestAcceptanceCriteriaFilterDTO}
                                obj={testAcceptanceCriteriaFilterDTO}
                                valueList={testComponentList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <CardList
                    columns={columns}
                    rows={testAcceptanceCriteriaList}
                    actions={cardActions}
                    hasChildren={false}
                    setRowExpanded={() => void undefined}
                    cardSize={12}
                />
                {newRecord && (
                    <TestAcceptanceCriteriaMaintenance
                        data={testScenarioId ?? data.id}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={data.testScenarioId ?? testAcceptanceCriteriaFilterDTO?.testScenarioId}

                    />
                )}
                {editRecord && (
                    <TestAcceptanceCriteriaMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'testingsphere.testacceptancecriteria'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};